import { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components/macro";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  BoxHeader,
  BoxTitle,
  BoxNavLeft,
  BoxNavRight,
  BoxContent,
} from "../Box";
import Stack from "../Stack";
import Button, { IconButton } from "../Button";
import Checkbox from "../Checkbox";
import { TrashCan, ChevronDown, Gear } from "../Icons";
import { THEME_COLOR } from "../../vars";
import Tooltip from "../Tooltip";
import { useFields } from "../../hooks/use-fields";
import Input, { Label } from "../Input";
import { useSkipioApi, touchSkipioApi } from "../../api";
import { transformKey, caseMethods } from "../../api/case";
import Conditions from "./Conditions";
import Switch from "../Switch";
import { useToast } from "../../hooks/use-toast";
import { useSkipioQuery } from "../../hooks/use-query";

function snakeCaseTopLevel(object) {
  const clone = {};
  for (let [key, value] of Object.entries(object)) {
    clone[transformKey(key, caseMethods.snake)] = value;
  }
  return clone;
}

function Automation({ automationAction, automation: initialValue, onSave }) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const automationMeta = useSkipioQuery({
    queryKey: ["automations", "meta"],
  }).data;

  const triggers = useMemo(() => snakeCaseTopLevel(automationMeta.triggers), [
    automationMeta,
  ]);

  const actions = useMemo(() => snakeCaseTopLevel(automationMeta.actions), [
    automationMeta,
  ]);

  const availableActions = Object.entries(
    actions
  ).filter(([_, actionDefinition]) =>
    Object.keys(actionDefinition.fields).every((key) => key in automationAction)
  );

  const [automation, onChange] = useState(initialValue);

  const { field, set, reset, isChanged } = useFields({
    value: automation,
    onChange,
  });

  // In the future this may be a separate component
  // but for now we only support a single action.
  // This is to drill down to the first action and
  // get its `field` function
  const { field: actionsField } = useFields(field("automationActions"));
  const { field: actionField } = useFields(actionsField(0));

  const [isExpanded, setIsExpanded] = useState(!initialValue.id);

  const sendToast = useToast();

  const toggleActive = async () => {
    set("active", !automation.active);

    if (isExpanded || !automation.id) {
      return;
    }

    await skipioApi.automations[automation.id].put(
      {},
      {
        data: { automation: { active: !automation.active } },
      }
    );

    sendToast(
      `Automation has is now ${automation.active ? "inactive" : "active"}.`,
      { success: true }
    );

    await touchSkipioApi("automations");
    await queryClient.invalidateQueries({ queryKey: ["automations"] });
  };

  const renderLogicToggle = () => {
    const switchComponent = (
      <Switch
        leftValue="or"
        leftLabel="or"
        rightValue="and"
        rightLabel="and"
        {...field("conditionLogic")}
      />
    );

    const length = automation.automationConditions.filter((c) => !c._destroy)
      .length;

    return length <= 2 ? (
      switchComponent
    ) : (
      <Tooltip label={`All conditions share either "and" or "or" logic.`}>
        {switchComponent}
      </Tooltip>
    );
  };

  const showToast = useToast();

  async function saveAutomation() {
    const caller = automation.id
      ? skipioApi.automations[automation.id].put
      : skipioApi.automations.post;

    try {
      await caller({}, { data: { automation } });
    } catch (e) {
      try {
        onChange(e.response.data.data);
        showToast(
          "Oops, we had an issue. Please fix the errors and try saving again.",
          {
            warning: true,
          }
        );

        return;
      } catch (_) {
        showToast("There was an error saving this automation.", {
          error: true,
        });

        throw e;
      }
    }

    showToast("Your automation was saved.", { success: true });

    await touchSkipioApi("users/me", "automations");
    await queryClient.invalidateQueries({ queryKey: ["users", "me"] });
    await queryClient.invalidateQueries({ queryKey: ["automations"] });
    onSave();
  }

  async function deleteAutomation() {
    if (
      !window.confirm("Are you sure you would like to remove this automation?")
    )
      return;

    await skipioApi.automations[automation.id].delete();
    showToast("Your automation was deleted.", { success: true });

    await touchSkipioApi("users/me", "automations");
    await queryClient.invalidateQueries({ queryKey: ["users", "me"] });
    await queryClient.invalidateQueries({ queryKey: ["automations"] });
  }

  return (
    <>
      <Box elevated>
        <BoxHeader white>
          <BoxNavLeft>
            <BoxTitle>{automation.name || "Untitled Automation"}</BoxTitle>
          </BoxNavLeft>
          <BoxNavRight style={{ paddingRight: "8px" }}>
            <Stack space={1} horizontal>
              {automation.active ? (
                <Tooltip label="Automation is active">
                  <IconButton
                    onClick={toggleActive}
                    style={{ color: THEME_COLOR }}
                  >
                    {automation.id ? <SpinningGear /> : <Gear />}
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip label="Automation is inactive">
                  <IconButton onClick={toggleActive}>
                    <Gear />
                  </IconButton>
                </Tooltip>
              )}
              {Boolean(automation.id) && (
                <IconButton onClick={deleteAutomation}>
                  <TrashCan />
                </IconButton>
              )}
              <IconButton
                onClick={
                  isExpanded
                    ? () => setIsExpanded(false)
                    : () => setIsExpanded(true)
                }
              >
                <ChevronDown
                  style={{
                    transform: `rotate(${isExpanded ? "0" : "-90"}deg)`,
                  }}
                />
              </IconButton>
            </Stack>
          </BoxNavRight>
        </BoxHeader>

        {isExpanded && (
          <>
            <BoxContent>
              <Input
                label="Automation Name"
                {...field("name")}
                required
                error={(automation.errors || {}).name}
              />
            </BoxContent>
            <Stack>
              <>
                <BoxHeader white>
                  <BoxNavLeft>
                    <BoxTitle>When should this automation run?</BoxTitle>
                  </BoxNavLeft>
                </BoxHeader>
                <BoxContent>
                  <Stack>
                    <Input
                      label="Run this automation..."
                      type="select"
                      {...field("trigger")}
                    >
                      {Object.entries(triggers).map(([value, { label }]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Input>
                    <Stack space={1}>
                      <Label>
                        How many times would you like this automation to run?
                      </Label>
                      <Checkbox
                        value={automation.oncePerContact}
                        onChange={() => field("oncePerContact").onChange(true)}
                      >
                        Run only once per contact
                        <FadedParagraph>
                          When selected, this automation will run on every
                          contact that has not already been through this
                          automation.
                        </FadedParagraph>
                      </Checkbox>

                      <Checkbox
                        value={!automation.oncePerContact}
                        onChange={() => field("oncePerContact").onChange(false)}
                      >
                        Run each time a contact matches the conditions
                        <FadedParagraph>
                          When selected, this automation will run every time a
                          contact matches the conditions.
                        </FadedParagraph>
                      </Checkbox>
                    </Stack>
                  </Stack>
                </BoxContent>
              </>

              <>
                <BoxHeader white>
                  <BoxNavLeft>
                    <BoxTitle>
                      What are the conditions of this automation?
                    </BoxTitle>
                  </BoxNavLeft>
                </BoxHeader>
                <BoxContent>
                  <Conditions
                    {...field("automationConditions")}
                    renderLogicToggle={renderLogicToggle}
                  />
                </BoxContent>
              </>

              {availableActions.length > 1 && (
                <>
                  <BoxHeader white>
                    <BoxNavLeft>
                      <BoxTitle>
                        What should happen once the conditions are met?
                      </BoxTitle>
                    </BoxNavLeft>
                  </BoxHeader>
                  <BoxContent>
                    <Input
                      type="select"
                      label="The contact should..."
                      {...actionField("name")}
                    >
                      {availableActions.map(([key, { label }]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </Input>
                  </BoxContent>
                </>
              )}

              <>
                <BoxContent>
                  <Stack horizontal grow={false}>
                    <Button onClick={saveAutomation} disabled={!isChanged}>
                      Save
                    </Button>
                    {isChanged && (
                      <Button variant="link" onClick={reset}>
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </BoxContent>
              </>
            </Stack>
          </>
        )}
      </Box>
    </>
  );
}

export default Automation;

const rotate = keyframes`
  from {
    transform: rotate(0deg) scale(.8);
  }
  to {
    transform: rotate(360deg) scale(.8);
  }
`;

const SpinningGear = styled(Gear)`
  animation: ${rotate} 3s linear infinite;
  animation-fill-mode: both;
`;

const FadedParagraph = styled.p`
  font-size: 0.95em;
  color: #676767;
`;
